import type { MigrateContext, Project } from '../services/project-service';

/**
 * Gate for the Azure Migrate panel on the project detail page (#412).
 *
 * Rule: hide the panel ONLY when the project demonstrably carries M365-plan
 * engagement substance and NO migration substance. The default is SHOW —
 * fail-open for the app's original Azure Migrate use case. Legacy projects
 * predate the engagement-type framework and may carry no engagement data at
 * all; they MUST keep the panel. Likewise any project where migrate state
 * exists (assessment project, appliance, discovery started, any non-default
 * migrate field) always keeps it — if someone has started discovery, never
 * hide their panel.
 *
 * The identifier sets mirror the backend engagement-type registry
 * (DocumentScaffoldService.EngagementTypes) — gate on IDs, never on display
 * labels. An identifier in neither set (e.g. "public-sector-za", or a future
 * type this file has not learnt about) contributes nothing, so on its own it
 * leaves the panel visible (fail-open).
 */

/** GMS M365 plans and add-ons — M365-plan engagement substance. */
export const M365_ENGAGEMENT_TYPES: readonly string[] = [
  'gms-plan-1-foundation',
  'gms-plan-2-endpoint',
  'gms-plan-3-information-governance',
  'entra-connect-hybrid-identity',
  'gms-mfa-deployment',
  'gms-ca-pim-deployment',
  'mailbox-migration',
  'security-posture-assessment',
];

/** Azure migration-flavoured engagements — migration substance. */
export const MIGRATION_ENGAGEMENT_TYPES: readonly string[] = [
  'azure-discovery',
  'azure-waf-assessment',
  'azure-migration',
];

/** Server-side defaults for MigrateContext — present on every context, so not substance. */
const DEFAULT_AUTH_MODE = 'architect-workstation';
const DEFAULT_APPLIANCE_STATUS = 'not-started';

/**
 * True when the MigrateContext carries any non-default field — i.e. someone
 * has actually recorded Azure Migrate state against the project.
 */
function hasMigrateState(ctx: MigrateContext | null | undefined): boolean {
  if (!ctx) return false;
  return Boolean(
    ctx.assessmentProjectName
    || ctx.resourceGroup
    || ctx.applianceName
    || ctx.tenantId
    || ctx.subscriptionId
    || ctx.discoveryStartedAt
    || (ctx.applianceStatus && ctx.applianceStatus !== DEFAULT_APPLIANCE_STATUS)
    || (ctx.authMode && ctx.authMode !== DEFAULT_AUTH_MODE),
  );
}

/**
 * Whether the project detail page should render the Azure Migrate panel
 * (and the Hypervisor field in the summary strip).
 *
 * Hidden iff hasM365Engagement && !hasMigrationSubstance; shown otherwise.
 */
export function showAzureMigratePanel(
  project: Pick<Project, 'engagementTypes' | 'migrateContext'>,
): boolean {
  const types = project.engagementTypes ?? [];
  const hasM365Engagement = types.some(t => M365_ENGAGEMENT_TYPES.includes(t));
  const hasMigrationSubstance =
    types.some(t => MIGRATION_ENGAGEMENT_TYPES.includes(t))
    || hasMigrateState(project.migrateContext);
  return !(hasM365Engagement && !hasMigrationSubstance);
}
